import api from './api';

const API_URL = '/transporteur/drivers';

// Get all chauffeurs
export const getChauffeurs = async (filters = {}) => {
  try {
    const response = await api.get(API_URL, { params: filters });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch chauffeurs');
  }
};

// Get a single chauffeur by ID
export const getChauffeur = async (id) => {
  try {
    const response = await api.get(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Chauffeur not found');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch chauffeur');
    }
  }
};

// Create a new chauffeur
export const createChauffeur = async (chauffeurData) => {
  try {
    const response = await api.post(API_URL, chauffeurData);
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error(error.response.data.message || 'Invalid chauffeur data');
    } else if (error.response?.status === 409) {
      throw new Error('A chauffeur with this email already exists');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to create chauffeur');
    }
  }
};

// Update chauffeur information
export const updateChauffeur = async (id, chauffeurData) => {
  try {
    const response = await api.put(`${API_URL}/${id}`, chauffeurData);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Chauffeur not found');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data.message || 'Invalid chauffeur data');
    } else if (error.response?.status === 409) {
      throw new Error('A chauffeur with this email already exists');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to update chauffeur');
    }
  }
};

// Delete a chauffeur
export const deleteChauffeur = async (id) => {
  try {
    const response = await api.delete(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Chauffeur not found');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to delete chauffeur');
    }
  }
};

// Get chauffeur's missions
export const getChauffeurMissions = async (id) => {
  try {
    const response = await api.get(`/transporteur/drivers/${id}/missions`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch chauffeur missions');
    }
  }
};

// Update chauffeur availability
export const updateChauffeurAvailability = async (id, isAvailable) => {
  try {
    const response = await api.put(`${API_URL}/${id}/availability`, { isAvailable });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Chauffeur not found');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to update chauffeur availability');
    }
  }
};

// Documents
export const getChauffeurDocuments = async (chauffeurId) => {
  try {
    const response = await api.get(`/transporteur/drivers/${chauffeurId}/documents`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch documents');
  }
};

export const uploadChauffeurDocument = async (chauffeurId, file, metadata = {}) => {
  try {
    if (!file) throw new Error('No file selected');
    const formData = new FormData();
    formData.append('file', file);
    if (metadata.name) formData.append('name', metadata.name);
    if (metadata.type) formData.append('type', metadata.type);
    const response = await api.post(`/transporteur/drivers/${chauffeurId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to upload document');
  }
};

export const deleteChauffeurDocument = async (documentId) => {
  try {
    await api.delete(`/transporteur/documents/${documentId}`);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete document');
  }
};

export const downloadChauffeurDocument = async (documentId) => {
  try {
    const response = await api.get(`/transporteur/documents/${documentId}/download`, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to download document');
  }
};

export const setDriverActive = async (id, active) => {
  try {
    await api.put(`/transporteur/drivers/${id}/activation?active=${active}`);
  } catch (error) {
    throw error;
  }
};

const chauffeurService = {
  getAllDrivers: async (transporteurId) => {
    try {
      const response = await api.get('/transporteur/drivers', {
        params: { transporteurId }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getDriverById: async (id) => {
    try {
      const response = await api.get(`/transporteur/drivers/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  addDriver: async (driverData) => {
    try {
      const response = await api.post('/transporteur/drivers', driverData);
      return response.data;
    } catch (error) {
      // Surface backend validation messages (including X-Error-Message header) as clear errors
      const headerMessage = error.response?.headers?.['x-error-message'];
      if (headerMessage) {
        throw new Error(headerMessage);
      }
      const bodyMessage = error.response?.data?.message;
      if (bodyMessage) {
        throw new Error(bodyMessage);
      }
      throw new Error(error.message || 'Failed to create chauffeur');
    }
  },

  updateDriver: async (id, driverData) => {
    try {
      const response = await api.put(`/transporteur/drivers/${id}`, driverData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteDriver: async (id) => {
    try {
      await api.delete(`/transporteur/drivers/${id}`);
    } catch (error) {
      throw error;
    }
  },

  updateDriverAvailability: async (id, disponible) => {
    try {
      await api.put(`/transporteur/drivers/${id}/availability?disponible=${disponible}`);
    } catch (error) {
      throw error;
    }
  },

  setDriverActive: async (id, active) => {
    try {
      await api.put(`/transporteur/drivers/${id}/activation?active=${active}`);
    } catch (error) {
      throw error;
    }
  },

  resetDriverPassword: async (id, newPassword) => {
    try {
      const response = await api.put(`/transporteur/drivers/${id}/reset-password`, { newPassword });
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to reset password';
      throw new Error(msg);
    }
  },

  getDriverDocuments: async (id) => {
    try {
      const response = await api.get(`/transporteur/drivers/${id}/documents`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getChauffeurMissions,
};

export const resetDriverPassword = chauffeurService.resetDriverPassword;

export default chauffeurService; 